import { generateGuid } from 'guid';
import { type LocalePackContentItem, type MinimalContentItem, type MinimalLocalePack } from './ContentItemEditor.types';

/*
    The locale pack bookkeeping the Iguana locale_pack setter and the is_content_item mixin used to
    do, as pure functions over the payload.

    A locale pack ties the translations of one content item together. Its content_items list holds
    one entry per locale, each `{ id, title, locale }`. The server reconciles the pack from that
    list on save and detaches anything the list leaves out, so the list is always complete.
*/

// The Angular mixin allowed a locale change only when this item was alone in its pack. Changing the
// locale of one item in a pack of translations would collide with a sibling.
export function canChangeLocale(pack: MinimalLocalePack | null | undefined): boolean {
    if (!pack) return true;
    return (pack.content_items || []).length === 1;
}

export function contentItemForLocale<Pack extends MinimalLocalePack>(
    pack: Pack | null | undefined,
    locale: string,
): LocalePackContentItem | undefined {
    return (pack?.content_items || []).find(contentItem => contentItem.locale === locale);
}

// The English item of a pack, seen from a translation. The Angular getter returned nothing for an
// English item, because an English item is not a translation of anything.
export function englishTranslation<Pack extends MinimalLocalePack>(
    item: MinimalContentItem,
    pack: Pack | null | undefined,
): LocalePackContentItem | undefined {
    if (item.locale === 'en') return undefined;
    return contentItemForLocale(pack, 'en');
}

// Links a translation into the pack, or unlinks it when newContentItem is null. Any entry already
// holding that locale gives way, which is what makes a link replace a translation.
export function setContentItemForLocale<Pack extends MinimalLocalePack>(
    pack: Pack,
    locale: string,
    newContentItem: LocalePackContentItem | null,
): Pack {
    const others = (pack.content_items || []).filter(contentItem => contentItem.locale !== locale);

    return {
        ...pack,
        content_items: newContentItem ? [...others, { ...newContentItem, locale }] : others,
    };
}

// A pack for an item that has none, with the item itself as its only entry. The id is generated on
// the client, as ensureLocalePack did.
export function buildLocalePack(item: MinimalContentItem): MinimalLocalePack {
    return {
        id: generateGuid(),
        content_items: [{ id: item.id, title: item.title, locale: item.locale }],
    };
}

// What the Angular locale watcher did when the admin changed the locale of the item being edited.
//
// Going to English restores the pack the item had when the page loaded, so the links it used to
// have come back, and creates a pack if there was none. Going to any other locale drops the pack:
// a translation cannot be the English item of the pack it belongs to, and the admin links it to an
// English item from that item's own page.
export function localePackForLocaleChange<Pack extends MinimalLocalePack>({
    item,
    newLocale,
    originalPack,
}: {
    item: MinimalContentItem;
    newLocale: string;
    originalPack: Pack | null | undefined;
}): Pack | MinimalLocalePack | null {
    if (newLocale !== 'en') return null;
    if (originalPack) return originalPack;

    return buildLocalePack({ ...item, locale: newLocale });
}
